import type { Fields } from '@puckeditor/core';

import { createFlatSpacingFields, createSpacingFields } from './spacing-fields';
import type { FlatSpacingProps } from './spacing-fields';
import type { BoxSpacing } from './tokens';
import { toggleField } from './widgets/toggle-field';

export type MobilePaddingProps = {
  differentMobilePadding: boolean;
  mobilePadding: BoxSpacing;
};

export function createMobilePaddingFields({ max }: { max?: number } = {}): Fields<MobilePaddingProps> {
  return {
    differentMobilePadding: toggleField('Different mobile padding'),
    mobilePadding: {
      type: 'object',
      label: 'Mobile padding',
      objectFields: createSpacingFields({ max }),
    },
  };
}

/** Hiding the controls leaves their saved values intact for the next time the toggle is enabled. */
export function resolveMobilePaddingFields<Props extends MobilePaddingProps>(
  enabled: unknown,
  fields: Fields<Props>
): Fields<Props> {
  if (enabled === true) return fields;

  return Object.fromEntries(Object.entries(fields).filter(([name]) => name !== 'mobilePadding')) as Fields<Props>;
}

export type FlatMobilePaddingProps = FlatSpacingProps<'mobilePadding'> & { differentMobilePadding: boolean };

export function createFlatMobilePaddingFields({
  max,
  labelPrefix,
}: { max?: number; labelPrefix?: string } = {}): Fields<FlatMobilePaddingProps> {
  return {
    differentMobilePadding: toggleField('Different mobile padding'),
    ...createFlatSpacingFields('mobilePadding', { max, labelPrefix }),
  };
}

export function resolveFlatMobilePaddingFields<Props extends FlatMobilePaddingProps>(
  enabled: unknown,
  fields: Fields<Props>
): Fields<Props> {
  if (enabled === true) return fields;
  const {
    mobilePaddingTop: _top,
    mobilePaddingRight: _right,
    mobilePaddingBottom: _bottom,
    mobilePaddingLeft: _left,
    ...visible
  } = fields;
  return visible as Fields<Props>;
}

/** Only absent sides are initialized; hiding the controls never discards authored values. */
export function fillFlatMobilePadding<Props extends Partial<FlatMobilePaddingProps>>(
  props: Props,
  defaults: BoxSpacing
): Props {
  if (props.differentMobilePadding !== true) return props;
  return {
    ...props,
    mobilePaddingTop: typeof props.mobilePaddingTop === 'undefined' ? defaults.top : props.mobilePaddingTop,
    mobilePaddingRight: typeof props.mobilePaddingRight === 'undefined' ? defaults.right : props.mobilePaddingRight,
    mobilePaddingBottom: typeof props.mobilePaddingBottom === 'undefined' ? defaults.bottom : props.mobilePaddingBottom,
    mobilePaddingLeft: typeof props.mobilePaddingLeft === 'undefined' ? defaults.left : props.mobilePaddingLeft,
  };
}
